import { createContext, useCallback, useContext, useEffect, useMemo, useState } from "react";
import type { ReactNode } from "react";
import type { Session } from "@supabase/supabase-js";
import { createIsolatedAuthClient, supabase } from "@/lib/supabase";
import type { UserRole } from "@/lib/supabase";
import { impersonationHome } from "./roles";
import {
  ADMIN_SESSION_KEY,
  IMPERSONATION_AUTH_KEY,
  IMPERSONATION_META_KEY,
  clearImpersonation,
  readImpersonation,
  type ImpersonatedUser,
} from "./impersonation-storage";
export type { ImpersonatedUser } from "./impersonation-storage";

interface StartImpersonationInput {
  salarieId?: string;
  userId?: string;
  displayName?: string;
}
interface ImpersonationContextValue {
  impersonated: ImpersonatedUser | null;
  busy: boolean;
  startImpersonating: (input: StartImpersonationInput) => Promise<void>;
  stopImpersonating: () => Promise<void>;
}
const ImpersonationContext = createContext<ImpersonationContextValue | null>(null);

async function invokeError(error: unknown): Promise<string> {
  const ctx = (error as { context?: unknown } | null)?.context;
  if (ctx instanceof Response) {
    try {
      const body = await ctx.clone().json();
      if (typeof body?.error === "string") return body.error;
    } catch {
      /* Corps non JSON : message générique. */
    }
  }
  return error instanceof Error ? error.message : "Impersonation impossible";
}

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const [impersonated] = useState(readImpersonation);
  const [busy, setBusy] = useState(false);

  const startImpersonating = useCallback(async (input: StartImpersonationInput) => {
    if (readImpersonation())
      throw new Error("Quittez la session actuelle avant de changer de compte");
    setBusy(true);
    try {
      const { data: current } = await supabase.auth.getSession();
      if (!current.session) throw new Error("Session administrateur introuvable");
      const { data, error } = await supabase.functions.invoke<{
        success: boolean;
        email: string;
        tokenHash?: string;
        verifyType?: "magiclink" | "invite";
        displayName: string;
        userId: string;
        session?: Session;
        returnAt?: number;
      }>("admin-impersonate", { body: { salarieId: input.salarieId, userId: input.userId } });
      if (error) throw new Error(await invokeError(error));
      if (!data?.success) throw new Error("Réponse d'impersonation invalide");
      let target = data.session;
      if (!target && data.tokenHash) {
        const { data: verified, error: verificationError } =
          await createIsolatedAuthClient().auth.verifyOtp({
            token_hash: data.tokenHash,
            type: data.verifyType ?? "magiclink",
          });
        if (verificationError) throw verificationError;
        target = verified.session ?? undefined;
      }
      if (!target || target.user.id !== data.userId) throw new Error("Session cible incohérente");
      const { data: targetRoles, error: rolesError } = await createIsolatedAuthClient(
        target.access_token,
      )
        .from("user_roles")
        .select("role")
        .eq("user_id", target.user.id);
      if (rolesError) throw new Error("Impossible de charger l'espace du compte cible");
      const meta: ImpersonatedUser = {
        id: target.user.id,
        email: data.email,
        displayName: input.displayName || data.displayName || data.email,
        returnAt: data.returnAt ?? (target.expires_at ? target.expires_at - 120 : undefined),
        isolated: true,
        debug: Boolean(data.session),
        storageKey: `${IMPERSONATION_AUTH_KEY}-${crypto.randomUUID()}`,
      };
      sessionStorage.setItem(meta.storageKey!, JSON.stringify(target));
      sessionStorage.setItem(IMPERSONATION_META_KEY, JSON.stringify(meta));
      // Reconstruit les clients et vide les caches métier. La session normale
      // en localStorage et les autres onglets n'ont jamais changé d'identité.
      window.location.assign(impersonationHome((targetRoles ?? []).map((r) => r.role)));
    } catch (error) {
      clearImpersonation();
      throw error;
    } finally {
      setBusy(false);
    }
  }, []);

  const stopImpersonating = useCallback(async () => {
    setBusy(true);
    try {
      if (readImpersonation()?.isolated) {
        // Une perte de réseau ou l'expiration ne doit pas bloquer le retour.
        if (readImpersonation()?.debug) {
          await Promise.race([
            supabase.functions.invoke("admin-impersonate", { body: { action: "stop" } }),
            new Promise((resolve) => window.setTimeout(resolve, 5000)),
          ]);
        } else await supabase.auth.signOut({ scope: "local" });
      } else {
        // Compatibilité avec les sessions ouvertes avant cette livraison.
        const saved = sessionStorage.getItem(ADMIN_SESSION_KEY);
        if (saved) {
          const { error } = await supabase.auth.setSession(JSON.parse(saved));
          if (error) await supabase.auth.signOut({ scope: "local" });
        } else await supabase.auth.signOut({ scope: "local" });
      }
    } finally {
      clearImpersonation();
      window.location.assign("/app/admin/utilisateurs");
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (!impersonated?.returnAt) return;
    const check = () => {
      if (Date.now() >= impersonated.returnAt! * 1000) void stopImpersonating();
    };
    check();
    const timer = window.setInterval(check, 10_000);
    window.addEventListener("focus", check);
    return () => {
      window.clearInterval(timer);
      window.removeEventListener("focus", check);
    };
  }, [impersonated, stopImpersonating]);

  const value = useMemo(
    () => ({ impersonated, busy, startImpersonating, stopImpersonating }),
    [impersonated, busy, startImpersonating, stopImpersonating],
  );
  return <ImpersonationContext.Provider value={value}>{children}</ImpersonationContext.Provider>;
}

export function useImpersonation() {
  const ctx = useContext(ImpersonationContext);
  if (!ctx) throw new Error("useImpersonation must be used within ImpersonationProvider");
  return ctx;
}

export async function fetchUserRoles(userId: string): Promise<UserRole[]> {
  const { data, error } = await supabase
    .from("user_roles")
    .select("role")
    .eq("user_id", userId)
    .returns<Array<{ role: UserRole }>>();
  if (error) throw error;
  return (data ?? []).map((r) => r.role);
}
